import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import type { MCPConfig } from "@/types";
import type { Dispatch, SetStateAction } from "react";
import { useState, useEffect } from "react";

interface MCPServersTabProps {
  mcpConfig: MCPConfig;
  setMcpConfig: Dispatch<SetStateAction<MCPConfig>>;
  defaultMCPConfig: MCPConfig;
  saveMCPConfig: (config: MCPConfig) => void;
  toast: any;
}

export const MCPServersTab = ({
  mcpConfig,
  setMcpConfig,
  defaultMCPConfig,
  saveMCPConfig,
  toast,
}: MCPServersTabProps) => {
  const [savedConfig, setSavedConfig] = useState<MCPConfig>(mcpConfig);
  const isDirty = JSON.stringify(mcpConfig) !== JSON.stringify(savedConfig);
  const handleSave = () => {
    try {
      saveMCPConfig(mcpConfig);
      setSavedConfig(mcpConfig); // Update saved state
      toast.success("MCP settings saved", {
        description: `Keep-alive: ${mcpConfig.keepAlive ? "enabled" : "disabled"}, Base delay: ${mcpConfig.baseDelay}s, Max attempts: ${mcpConfig.maxAttempts}`
      });
      // Dispatch event so ChatWindow can pick up changes
      window.dispatchEvent(new CustomEvent("mcp-config-updated", { detail: mcpConfig }));
    } catch (error) {
      toast.error("Failed to save MCP settings", {
        description: error instanceof Error ? error.message : "Unknown error"
      });
    }
  };
  const handleReset = () => {
    setMcpConfig(savedConfig);
  };
  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="rounded-lg sm:rounded-xl border border-border/70 bg-white/80 p-3 sm:p-4 shadow-sm dark:bg-white/5">
        <h4 className="text-xs sm:text-sm font-semibold text-foreground">Connection & Keep-Alive</h4>
        <p className="text-[10px] sm:text-sm text-muted-foreground mb-3">Configure how chatMCP maintains connections to MCP servers.</p>
        
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/30 px-2 sm:px-3 py-2">
            <Checkbox
              checked={mcpConfig.keepAlive}
              onChange={event => setMcpConfig((prev: MCPConfig) => ({ ...prev, keepAlive: event.target.checked }))}
            />
            <div className="space-y-0.5 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-foreground">Enable keep-alive</p>
              <p className="text-[9px] sm:text-xs text-muted-foreground">
                Periodically ping servers to maintain connections and detect offline status.
              </p>
            </div>
          </div>

          <div className="grid gap-2 sm:gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-[10px] sm:text-sm font-medium text-foreground">Base delay (seconds)</label>
              <Input
                type="number"
                min="10"
                max="300"
                value={mcpConfig.baseDelay}
                onChange={e => setMcpConfig((prev: MCPConfig) => ({ ...prev, baseDelay: Number(e.target.value) }))}
                placeholder="120"
                className="h-8 sm:h-9 text-xs sm:text-sm"
                disabled={!mcpConfig.keepAlive}
              />
              <p className="text-[9px] sm:text-xs text-muted-foreground">Initial delay between ping attempts (10-300s).</p>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] sm:text-sm font-medium text-foreground">Max delay (seconds)</label>
              <Input
                type="number"
                min="60"
                max="1800"
                value={mcpConfig.maxDelay}
                onChange={e => setMcpConfig((prev: MCPConfig) => ({ ...prev, maxDelay: Number(e.target.value) }))}
                placeholder="600"
                className="h-8 sm:h-9 text-xs sm:text-sm"
                disabled={!mcpConfig.keepAlive}
              />
              <p className="text-[9px] sm:text-xs text-muted-foreground">Maximum delay with exponential backoff (60-1800s).</p>
            </div>
          </div>

          <div className="grid gap-2 sm:gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-[10px] sm:text-sm font-medium text-foreground">Max retry attempts</label>
              <Input
                type="number"
                min="1"
                max="10"
                value={mcpConfig.maxAttempts}
                onChange={e => setMcpConfig((prev: MCPConfig) => ({ ...prev, maxAttempts: Number(e.target.value) }))}
                placeholder="4"
                className="h-8 sm:h-9 text-xs sm:text-sm"
                disabled={!mcpConfig.keepAlive}
              />
              <p className="text-[9px] sm:text-xs text-muted-foreground">Number of retry attempts before marking offline (1-10).</p>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] sm:text-sm font-medium text-foreground">Connection timeout (seconds)</label>
              <Input
                type="number"
                min="5"
                max="120"
                value={mcpConfig.connectionTimeout}
                onChange={e => setMcpConfig((prev: MCPConfig) => ({ ...prev, connectionTimeout: Number(e.target.value) }))}
                placeholder="30"
                className="h-8 sm:h-9 text-xs sm:text-sm"
              />
              <p className="text-[9px] sm:text-xs text-muted-foreground">Timeout for establishing connections (5-120s).</p>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] sm:text-sm font-medium text-foreground">Request timeout (seconds)</label>
            <Input
              type="number"
              min="10"
              max="300"
              value={mcpConfig.requestTimeout}
              onChange={e => setMcpConfig((prev: MCPConfig) => ({ ...prev, requestTimeout: Number(e.target.value) }))}
              placeholder="60"
              className="h-8 sm:h-9 text-xs sm:text-sm"
            />
            <p className="text-[9px] sm:text-xs text-muted-foreground">Timeout for individual API requests (10-300s).</p>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/30 px-2 sm:px-3 py-2">
            <Checkbox
              checked={mcpConfig.autoReconnect}
              onChange={event => setMcpConfig((prev: MCPConfig) => ({ ...prev, autoReconnect: event.target.checked }))}
            />
            <div className="space-y-0.5 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-foreground">Auto-reconnect</p>
              <p className="text-[9px] sm:text-xs text-muted-foreground">
                Automatically attempt to reconnect when server comes back online.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/30 px-2 sm:px-3 py-2">
            <Checkbox
              checked={mcpConfig.enableLogging}
              onChange={event => setMcpConfig((prev: MCPConfig) => ({ ...prev, enableLogging: event.target.checked }))}
            />
            <div className="space-y-0.5 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-foreground">Enable connection logging</p>
              <p className="text-[9px] sm:text-xs text-muted-foreground">
                Log connection events and errors to browser console.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg sm:rounded-xl border border-border/70 bg-white/80 p-3 sm:p-4 shadow-sm dark:bg-white/5">
        <h4 className="text-xs sm:text-sm font-semibold text-foreground">Future Features (Coming Soon)</h4>
        <p className="text-[10px] sm:text-sm text-muted-foreground mb-3">These features are planned for future releases.</p>
        
        <div className="space-y-3 sm:space-y-4 opacity-60">
          <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/30 px-2 sm:px-3 py-2">
            <Checkbox
              checked={mcpConfig.enableMetrics}
              onChange={event => setMcpConfig((prev: MCPConfig) => ({ ...prev, enableMetrics: event.target.checked }))}
              disabled
            />
            <div className="space-y-0.5 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-foreground">Performance metrics</p>
              <p className="text-[9px] sm:text-xs text-muted-foreground">
                Collect and display performance metrics for MCP servers (latency, success rate, etc.).
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/30 px-2 sm:px-3 py-2">
            <Checkbox
              checked={mcpConfig.enableCaching}
              onChange={event => setMcpConfig((prev: MCPConfig) => ({ ...prev, enableCaching: event.target.checked }))}
              disabled
            />
            <div className="space-y-0.5 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-foreground">Response caching</p>
              <p className="text-[9px] sm:text-xs text-muted-foreground">
                Cache MCP responses to reduce latency and server load.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/30 px-2 sm:px-3 py-2">
            <Checkbox
              checked={mcpConfig.compressionEnabled}
              onChange={event => setMcpConfig((prev: MCPConfig) => ({ ...prev, compressionEnabled: event.target.checked }))}
              disabled
            />
            <div className="space-y-0.5 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-foreground">Compression</p>
              <p className="text-[9px] sm:text-xs text-muted-foreground">
                Enable gzip compression for request/response payloads.
              </p>
            </div>
          </div>

          <div className="grid gap-2 sm:gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-[10px] sm:text-sm font-medium text-foreground">Rate limit (requests/min)</label>
              <Input
                type="number"
                value={mcpConfig.rateLimitPerMinute}
                onChange={e => setMcpConfig((prev: MCPConfig) => ({ ...prev, rateLimitPerMinute: Number(e.target.value) }))}
                placeholder="60"
                className="h-8 sm:h-9 text-xs sm:text-sm"
                disabled
              />
              <p className="text-[9px] sm:text-xs text-muted-foreground">Limit requests per minute to prevent overload.</p>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] sm:text-sm font-medium text-foreground">Max concurrent requests</label>
              <Input
                type="number"
                value={mcpConfig.maxConcurrentRequests}
                onChange={e => setMcpConfig((prev: MCPConfig) => ({ ...prev, maxConcurrentRequests: Number(e.target.value) }))}
                placeholder="5"
                className="h-8 sm:h-9 text-xs sm:text-sm"
                disabled
              />
              <p className="text-[9px] sm:text-xs text-muted-foreground">Maximum parallel requests to a single server.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={handleReset} disabled={!isDirty} className="h-8 sm:h-9 text-xs sm:text-sm">
          Reset
        </Button>
        <Button onClick={handleSave} disabled={!isDirty} className="h-8 sm:h-9 text-xs sm:text-sm">
          Save MCP settings
        </Button>
      </div>
    </div>
  );
};
